import React, { FC, useEffect, useState, useDeferredValue } from 'react';
import { Box, Combobox, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import { CostCodeList, Scalars } from '@bechtel/types';
import { useField } from 'formik';
import { NetworkStatus } from '@apollo/client';
import { get } from 'lodash';
import { useLingui } from '@lingui/react';
import { ErrorRetry } from '@bechtel/client-common';
import { t, Trans } from '@lingui/macro';
import { useCostCodesQuery } from '~/hooks';

const ItemComponent = ({ item }: any) => {
  return (
    <Box css={{ width: '100%', whiteSpace: 'nowrap' }} alignItems={{ sm: 'baseline' }}>
      <Text size="400" color="grey.1000">
        {item.costCode}
      </Text>
      &nbsp;
      {(item.invalid || item.restricted || item.unavailable) && (
        <Text size="300" color="grey.600">
          ({item.invalid ? t`Invalid` : item.restricted ? t`Restricted` : item.unavailable ? t`Unavailable` : ''})
        </Text>
      )}
    </Box>
  );
};
const SelectedItemComponent = ({ item }: any) => {
  return (
    <Box css={{ width: '100%' }} justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
      <Text size="400" family="graphik" color="grey.1000">
        {item?.costCode}
      </Text>
    </Box>
  );
};

interface UseSelectorProps {
  name: string;
  crewId: Scalars['ID'];
  crewMemberId?: Scalars['ID'];
  isDisabled?: boolean;
}
export const CostCodes: FC<UseSelectorProps> = ({ name, crewId, crewMemberId, isDisabled }) => {
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDeferredValue(searchText?.trim());
  const { i18n } = useLingui();
  const [getCostCodeList, { data, loading, error, refetch, networkStatus }] = useCostCodesQuery(true);
  const costCodeList = get(data, 'costCodes.items', []) as CostCodeList['items'];

  useEffect(() => {
    if (debouncedSearchText && debouncedSearchText.length < 3) return;
    getCostCodeList({
      variables: {
        input: {
          costCode: debouncedSearchText,
          crewId: crewId,
          // crewMemberId: crewMemberId,
        },
        paging: {
          cursor: '1',
          limit: 20,
        },
      },
    });
  }, [debouncedSearchText, getCostCodeList]);
  const itemToString = (item) => item?.costCode;

  return (
    <Box
      my={{ sm: '500' }}
      mr={{ sm: '450' }}
      ml={{ sm: '550' }}
      flexDirection={{ sm: 'column' }}
      data-testid="cost-codes"
    >
      {!loading && error && (
        <ErrorRetry refetch={refetch}>
          <Trans>There was a problem loading cost codes. Please retry.</Trans>
        </ErrorRetry>
      )}
      <Combobox
        {...field}
        disabled={isDisabled}
        placeholder={t`Select`}
        ariaLabel={t`Select`}
        size={TextFieldSize.MEDIUM}
        items={costCodeList}
        appearance={TextFieldAppearance.NORMAL}
        itemComponent={ItemComponent}
        selectedComponent={SelectedItemComponent}
        selectedItem={field.value ? field.value : t`Select`}
        onSelect={async (selectedItem: any) => {
          setValue(selectedItem);
        }}
        term={searchText}
        setTerm={setSearchText}
        handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setSearchText(event.target.value);
        }}
        restorePreviousOnSkip
        loading={loading || networkStatus === NetworkStatus.refetch}
        messageOnNoResults={t`No Cost Code found`}
        itemString={itemToString}
      />
      {meta.touched && meta.error ? (
        <Box pt={{ sm: '200' }}>
          <Text color="bechtel.red">{i18n._(meta.error)}</Text>
        </Box>
      ) : null}
    </Box>
  );
};
